import React, { useRef } from "react"
import { Grid, IconButton, TextField, Button, Typography, InputAdornment } from "@material-ui/core"
import useContentEditor from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/hooks/useContentEditor"
import { ApiBlockCustomFieldWebhookAssociation, ChatBotFlowBlockContent } from "@/protocols/chatBot"
import useChatBotFlowConstructorStore from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/hooks/useChatBotFlowConstructorStore"
import { formatSaveInputLocationOptions, SaveInputLocationData } from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/utils/chatBotFlowBlockContent"
import BlockBodySectionDialogEditor from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/BlockBodySectionDialogEditor"
import WebhookFieldAssociationInput from "@/pages/Admin/Webhooks/WebhookFieldAssociationInput"
import { Autocomplete } from "@material-ui/lab"
import { Add, Close as CloseIcon } from "@material-ui/icons"
import useStyles from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/resources/Blocks/APIBlock/APIBlockCustomFieldSectionEditor/styles"

type APIBlockCustomFieldSectionEditorProps = {
	onSave: (chatBotFlowBlockContent: ChatBotFlowBlockContent) => void
	chatBotFlowBlockContent: ChatBotFlowBlockContent
}

const APIBlockCustomFieldSectionEditor: React.FC<APIBlockCustomFieldSectionEditorProps> = ({
	chatBotFlowBlockContent,
	onSave,
	children
}) => {
	const contentEditor = useContentEditor({ chatBotFlowBlockContent })
	const apiBlock = contentEditor.content?.apiBlock
	const customFieldWebhookAssociation = contentEditor.content?.apiBlock?.customFieldWebhookAssociation || []
	const classes = useStyles()
	const chatBotFlowConstructorStore = useChatBotFlowConstructorStore()
	const scrollContainerRef = useRef<HTMLDivElement>(null)

	const formattedOptions = formatSaveInputLocationOptions(chatBotFlowConstructorStore.constructionResources)

	const getSectionTitle = (type: ChatBotFlowBlockContent["locationToSaveInput"]["type"]) => ({
		"custom-field": "Campos Personalizados",
		client: "Campos Nativos"
	}[type])

	const handleChangeField = (index: number, selected: SaveInputLocationData) => {
		const prev = customFieldWebhookAssociation[index]

		const formatted: ApiBlockCustomFieldWebhookAssociation = {
			customFieldName: selected.name,
			type: selected.type,
			customFieldId: selected.customFieldId,
			webhookField: prev?.webhookField
		}

		const updated = [...customFieldWebhookAssociation]
		updated[index] = formatted

		contentEditor.changeContent({
			apiBlock: {
				...apiBlock,
				customFieldWebhookAssociation: updated
			}
		})
	}

	const handleChangeValue = (index: number, newValue: string) => {
		customFieldWebhookAssociation[index].webhookField = newValue

		contentEditor.changeContent({
			apiBlock: {
				...apiBlock,
				customFieldWebhookAssociation
			}
		})
	}

	const handleRemoveCustomFieldOption = (index: number) => {
		const newCustomFieldWebhookAssociation = [...customFieldWebhookAssociation]
		newCustomFieldWebhookAssociation.splice(index, 1)

		contentEditor.changeContent({
			apiBlock: {
				...apiBlock,
				customFieldWebhookAssociation: newCustomFieldWebhookAssociation
			}
		})
	}

	const handleAddCustomFieldOption = () => {
		const newCustomFieldWebhookAssociation: ApiBlockCustomFieldWebhookAssociation[] = [
			...customFieldWebhookAssociation,
			{
				customFieldName: "",
				type: "custom-field",
				webhookField: ""
			}
		]

		contentEditor.changeContent({
			apiBlock: {
				...apiBlock,
				customFieldWebhookAssociation: newCustomFieldWebhookAssociation
			}
		})

		setTimeout(() => {
			scrollContainerRef.current?.scrollTo({
				top: scrollContainerRef.current.scrollHeight,
				behavior: "smooth"
			})
		}, 100)
	}

	const handleOpen = () => contentEditor.reset()
	const handleSave = () => onSave(contentEditor.content)

	return (
		<BlockBodySectionDialogEditor
			title="Salvar resposta"
			onSave={handleSave}
			onOpen={handleOpen}
			ActionDialogProps={{
				children: (
					<Grid container direction="column" spacing={2}>
						<Grid item xs={12}>
							<Typography>
								Associe os campos da Letalk com os campos da estrutura de dados recebidos na resposta da requisição.
							</Typography>
						</Grid>
						<Grid item>
							<Button
								variant="outlined"
								color="primary"
								onClick={handleAddCustomFieldOption}
								style={{ height: 48 }}
								startIcon={<Add />}
							>
								Adicionar nova associação
							</Button>
						</Grid>
						<Grid item style={{ height: 400 }}>
							<Grid item xs={12} ref={scrollContainerRef} style={{ height: 400, maxHeight: 400, overflowY: "auto", overflowX: "hidden" }}>
								{customFieldWebhookAssociation.length > 0 ? <WebhookFieldAssociationInput
									label={{
										leftSide: "Campo personalizado na Letalk",
										rightSide: "Dados que recebemos na resposta da requisição"
									}}
									items={customFieldWebhookAssociation.map((data, index) => ({
										leftSide: (
											<Autocomplete
												key={`autocomplete-${index}`}
												value={formattedOptions.find(option => (
													option.customFieldId === data.customFieldId && option.type === data.type
												) || option.name === data.customFieldName) || undefined}
												options={formattedOptions}
												getOptionSelected={(option, value) => option?.customFieldId === value?.customFieldId}
												getOptionLabel={(option) => option?.name}
												fullWidth
												filterSelectedOptions
												groupBy={(option) => getSectionTitle(option?.type)}
												onChange={(_, selected) => {
													if (selected) handleChangeField(index, selected)
												}}
												onOpen={async () => {
													await chatBotFlowConstructorStore.loadSpecificConstructionResources("saveInputLocationOptions")
												}}
												renderInput={(params) => (
													<TextField
														className={classes.filterInput}
														variant="outlined"
														placeholder="Selecione o campo"
														{...{
															...params,
															InputProps: {
																...params.InputProps,
																className: classes.customFieldInput
															}
														}}
													/>
												)}
												size="small"
												disableClearable
											/>
										),
										rightSide: (
											<Grid container alignItems="center" key={`webhook-field-${index}`}>
												<Grid item xs={11}>
													<TextField
														variant="outlined"
														placeholder="contacts.cpf"
														value={data.webhookField || ""}
														onChange={({ target }) => handleChangeValue(index, target.value)}
														fullWidth
													/>
												</Grid>
												<Grid item xs={1}>
													<InputAdornment position="end">
														<IconButton size="small" onClick={() => handleRemoveCustomFieldOption(index)}>
															<CloseIcon fontSize="small" />
														</IconButton>
													</InputAdornment>
												</Grid>
											</Grid>
										)
									}))}
								/> : <Typography>Nenhuma associação configurada</Typography>}
							</Grid>
						</Grid>
					</Grid>
				)
			}}
		>
			{children}
		</BlockBodySectionDialogEditor>
	)
}

export default APIBlockCustomFieldSectionEditor
